import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { PdfCustomizationSettings } from "../../../common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
import {
  CommonMisconceptionResponseDto,
  MisconceptionItem,
} from "@packages/api/madlenAPI.schemas";

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface MisconceptionCardProps {
  misconception: CommonMisconceptionResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  misconceptionContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  section: {
    marginBottom: 12,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  // Assessment Strategies section styles
  assessmentContainer: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  assessmentTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#2563eb",
    marginBottom: 12,
    textAlign: "center",
  },
  assessmentMethodsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  methodsColumn: {
    flex: 1,
    paddingHorizontal: 8,
  },
  methodsHeader: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  detectionHeader: {
    backgroundColor: "#fed7aa",
    color: "#ea580c",
  },
  reevaluationHeader: {
    backgroundColor: "#dcfce7",
    color: "#16a34a",
  },
  methodItem: {
    marginBottom: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  detectionItem: {
    backgroundColor: "#fef3c7",
  },
  reevaluationItem: {
    backgroundColor: "#ecfdf5",
  },
  // Markdown content styling for methods
  methodMarkdownContent: {
    fontSize: 9,
    lineHeight: 1.4,
  },
  // Misconception section styles
  misconceptionHeader: {
    backgroundColor: "#fee2e2",
  },
  misconceptionTitle: {
    color: "#dc2626",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Clarification section styles
  clarificationHeader: {
    backgroundColor: "#dcfce7",
  },
  clarificationTitle: {
    color: "#16a34a",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Why occurs section styles
  whyHeader: {
    backgroundColor: "#dbeafe",
  },
  whyTitle: {
    color: "#2563eb",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Fix strategies section styles
  strategiesHeader: {
    backgroundColor: "#f3e8ff",
  },
  strategiesTitle: {
    color: "#9333ea",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Example section styles
  exampleHeader: {
    backgroundColor: "#fef3c7",
  },
  exampleTitle: {
    color: "#d97706",
    fontSize: 11,
    fontWeight: "bold",
  },
});

const MisconceptionDocument = ({
  misconception,
  customizationSettings,
}: MisconceptionCardProps) => {
  const { t } = useTranslation();

  const sectionContentMarkdown = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };

  // Markdown style for methods
  const methodMarkdownStyle = {
    text: {
      text: {
        fontSize: scaleFontSize(
          styles.methodMarkdownContent.fontSize,
          customizationSettings?.fontSize,
        ),
        lineHeight: styles.methodMarkdownContent.lineHeight,
      },
    },
  };

  return (
    <PdfLayout
      courseName={misconception.courseName || ""}
      gradeLevel={misconception.gradeLevel || 0}
      topic={misconception.title || misconception.prompt || ""}
      imageUrl={misconception.imageUrl}
      headerTextColor="#dc2626"
      customizationSettings={customizationSettings}
      author={misconception.authorName || ""}
      createdDate={misconception.createdAt || ""}
    >
      {/* Assessment Strategies Section */}
      {misconception.assessmentStrategies &&
        ((misconception.assessmentStrategies.detection_methods &&
          misconception.assessmentStrategies.detection_methods.length > 0) ||
          (misconception.assessmentStrategies.re_evaluation_methods &&
            misconception.assessmentStrategies.re_evaluation_methods.length >
              0)) && (
          <View style={styles.assessmentContainer} wrap={false}>
            <Text
              style={[
                styles.assessmentTitle,
                {
                  fontSize: scaleFontSize(
                    styles.assessmentTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {t("misconceptions.assessmentStrategies")}
            </Text>

            <View style={styles.assessmentMethodsContainer}>
              {/* Detection Methods */}
              {misconception.assessmentStrategies.detection_methods &&
                misconception.assessmentStrategies.detection_methods.length >
                  0 && (
                  <View style={styles.methodsColumn}>
                    <View
                      style={[styles.methodsHeader, styles.detectionHeader]}
                    >
                      <Text
                        style={[
                          {
                            fontSize: scaleFontSize(
                              12,
                              customizationSettings?.fontSize,
                            ),
                            fontWeight: "bold",
                          },
                        ]}
                      >
                        {t("misconceptions.detectionMethods")}
                      </Text>
                    </View>
                    {misconception.assessmentStrategies.detection_methods.map(
                      (method: string, index: number) => (
                        <View
                          key={index}
                          style={[styles.methodItem, styles.detectionItem]}
                        >
                          <MarkdownRenderer
                            content={method}
                            customStyle={methodMarkdownStyle}
                          />
                        </View>
                      ),
                    )}
                  </View>
                )}

              {/* Re-evaluation Methods */}
              {misconception.assessmentStrategies.re_evaluation_methods &&
                misconception.assessmentStrategies.re_evaluation_methods
                  .length > 0 && (
                  <View style={styles.methodsColumn}>
                    <View
                      style={[styles.methodsHeader, styles.reevaluationHeader]}
                    >
                      <Text
                        style={[
                          {
                            fontSize: scaleFontSize(
                              12,
                              customizationSettings?.fontSize,
                            ),
                            fontWeight: "bold",
                          },
                        ]}
                      >
                        {t("misconceptions.reEvaluationMethods")}
                      </Text>
                    </View>
                    {misconception.assessmentStrategies.re_evaluation_methods.map(
                      (method: string, index: number) => (
                        <View
                          key={index}
                          style={[styles.methodItem, styles.reevaluationItem]}
                        >
                          <MarkdownRenderer
                            content={method}
                            customStyle={methodMarkdownStyle}
                          />
                        </View>
                      ),
                    )}
                  </View>
                )}
            </View>
          </View>
        )}

      {misconception.misconceptions?.map(
        (item: MisconceptionItem, index: number) => (
          <View key={index} style={styles.misconceptionContainer} wrap={false}>
            {/* Misconception Section */}
            {item.misconception && (
              <View style={styles.section}>
                <View
                  style={[styles.sectionHeader, styles.misconceptionHeader]}
                >
                  <Text
                    style={[
                      styles.misconceptionTitle,
                      {
                        fontSize: scaleFontSize(
                          styles.misconceptionTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("misconceptions.commonMisconception")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={item.misconception}
                  customStyle={sectionContentMarkdown}
                />
              </View>
            )}

            {/* Clarification Section */}
            {item.clarification && (
              <View style={styles.section}>
                <View
                  style={[styles.sectionHeader, styles.clarificationHeader]}
                >
                  <Text
                    style={[
                      styles.clarificationTitle,
                      {
                        fontSize: scaleFontSize(
                          styles.clarificationTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("misconceptions.clarification")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={item.clarification}
                  customStyle={sectionContentMarkdown}
                />
              </View>
            )}

            {/* Why Occurs Section */}
            {item.why_misconception_occurs && (
              <View style={styles.section}>
                <View style={[styles.sectionHeader, styles.whyHeader]}>
                  <Text
                    style={[
                      styles.whyTitle,
                      {
                        fontSize: scaleFontSize(
                          styles.whyTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("misconceptions.whyOccurs")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={item.why_misconception_occurs}
                  customStyle={sectionContentMarkdown}
                />
              </View>
            )}

            {/* Fix Strategies Section */}
            {item.fix_strategies && (
              <View style={styles.section}>
                <View style={[styles.sectionHeader, styles.strategiesHeader]}>
                  <Text
                    style={[
                      styles.strategiesTitle,
                      {
                        fontSize: scaleFontSize(
                          styles.strategiesTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("misconceptions.fixStrategies")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={item.fix_strategies}
                  customStyle={sectionContentMarkdown}
                />
              </View>
            )}

            {/* Example Section */}
            {item.real_classroom_example && (
              <View style={styles.section}>
                <View style={[styles.sectionHeader, styles.exampleHeader]}>
                  <Text
                    style={[
                      styles.exampleTitle,
                      {
                        fontSize: scaleFontSize(
                          styles.exampleTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("misconceptions.connectionsExample")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={item.real_classroom_example}
                  customStyle={sectionContentMarkdown}
                />
              </View>
            )}
          </View>
        ),
      )}
    </PdfLayout>
  );
};

function MisconceptionCardPDF(props: MisconceptionCardProps) {
  return <MisconceptionDocument {...props} />;
}

export default MisconceptionCardPDF;
